import { Link } from "react-router";

import { Caution, HelpLayout, HelpSectionBlock } from "@/components/help-layout";
import { subText, table, td, th } from "@/components/table";
import { SEVERITY, SEVERITY_ORDER } from "@/lib/severity";

const SECTIONS = [
  { id: "flag", title: "What a risk flag is" },
  { id: "severity", title: "What each severity does" },
  { id: "lifecycle", title: "Raising, retiring, expiry" },
  { id: "access", title: "Access lists" },
];

export function RiskHelpPage() {
  return (
    <HelpLayout
      title="Risk flags and access lists"
      intro="A risk flag is a time-boxed note that HR is watching a situation. An access list is a group whose membership HR keeps. Both are applied by HROps within a few minutes and logged against the person who asked."
      sections={SECTIONS}
    >
      <HelpSectionBlock id="flag" title="What a risk flag is">
        <p>
          A flag names a person, a coded reason, a severity, a start and an end, and an optional
          note of up to 500 characters. Low and medium change nothing for the person; high restricts
          what they can do on their managed devices and in mail, and they will notice. Every flag
          expires, thirty days by default; there are no permanent lists.
        </p>
        <Caution>
          The flagged person is never told by HROps, and must not be told by the controls either. If
          you need somebody warned, that is a conversation, not a flag.
        </Caution>
      </HelpSectionBlock>

      <HelpSectionBlock id="severity" title="What each severity does">
        <table className={table}>
          <thead>
            <tr>
              <th className={th}>Severity</th>
              <th className={th}>Effect on the person</th>
            </tr>
          </thead>
          <tbody>
            {SEVERITY_ORDER.map((value) => (
              <tr key={value}>
                <td className={`${td} font-medium`}>{SEVERITY[value].label}</td>
                <td className={td}>
                  <p>{SEVERITY[value].summary}</p>
                  <ul className={`${subText} mt-1 list-disc pl-5`}>
                    {SEVERITY[value].effects.map((e) => (
                      <li key={e}>{e}</li>
                    ))}
                  </ul>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </HelpSectionBlock>

      <HelpSectionBlock id="lifecycle" title="Raising, retiring, expiry">
        <p>
          Raise a flag on the{" "}
          <Link className="underline" to="/flags">
            Risk flags
          </Link>{" "}
          page. A high flag is enforced within about five minutes. Retire it early from the same
          page; the restriction is lifted within the same few minutes. An expired flag lifts by
          itself. Holders of a directory role, protected administrators and break-glass accounts
          cannot be flagged high.
        </p>
      </HelpSectionBlock>

      <HelpSectionBlock id="access" title="Access lists">
        <p>
          An access list is one Entra group that HR maintains from the{" "}
          <Link className="underline" to="/access">
            Access lists
          </Link>{" "}
          page. Adding somebody gives them what the group grants; removing them, with a reason,
          takes it away; nothing else changes for the person. Only the groups set up for HROps
          appear, and there is no way to reach any other group from here. Guests, disabled accounts
          and the protected accounts are refused.
        </p>
      </HelpSectionBlock>
    </HelpLayout>
  );
}
